import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Rating,
  Snackbar,
  Alert,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { helpPageStyles } from "../../core/styles/cards/helpPageStyles";
import { FEEDBACK_TYPES } from "../configs/helpConfig";
import { submitSupportRequest } from "../../../utils/supportService";

/**
 * Feedback form. A low-friction way for staff to share ideas, flag confusing things, or give
 * praise — with an optional star rating. Submits to the same backend intake as support requests.
 */
const FeedbackForm = () => {
  const theme = useTheme();
  const styles = helpPageStyles(theme);

  const [type, setType] = useState(FEEDBACK_TYPES[0]);
  const [rating, setRating] = useState(0);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: "success", msg: "" });

  const canSubmit = message.trim().length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await submitSupportRequest({
        kind: "feedback",
        feedbackType: type,
        rating: rating || undefined,
        message: message.trim(),
      });
      setSnack({ open: true, severity: "success", msg: "Thanks for the feedback — we read all of it." });
      setMessage("");
      setRating(0);
      setType(FEEDBACK_TYPES[0]);
    } catch (e) {
      setSnack({ open: true, severity: "error", msg: "Sorry, that didn't send. Please try again in a moment." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={styles.mdFormCard}>
      <Typography sx={styles.mdFormTitle}>Share feedback</Typography>
      <Typography sx={styles.mdFormSubtitle}>
        Ideas, rough edges, or something you love — it shapes what we build next.
      </Typography>

      <TextField
        select
        fullWidth
        size="small"
        label="Type"
        value={type}
        onChange={(e) => setType(e.target.value)}
        sx={styles.mdFormField}
      >
        {FEEDBACK_TYPES.map((t) => (
          <MenuItem key={t} value={t}>
            {t}
          </MenuItem>
        ))}
      </TextField>

      <Box sx={styles.mdRatingRow}>
        <Typography sx={styles.mdRatingLabel}>How's your experience? (optional)</Typography>
        <Rating value={rating} onChange={(e, v) => setRating(v || 0)} />
      </Box>

      <TextField
        fullWidth
        multiline
        minRows={4}
        label="Your feedback"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        sx={styles.mdFormField}
      />

      <Button
        variant="contained"
        disabled={!canSubmit}
        onClick={handleSubmit}
        sx={styles.mdFormButton}
      >
        {submitting ? "Sending…" : "Send feedback"}
      </Button>

      <Snackbar
        open={snack.open}
        autoHideDuration={6000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FeedbackForm;
